import type {
  AccountJyotishPeriod,
  AccountJyotishSnapshot,
  AccountJyotishTransit,
  AccountReading,
} from "./account-api";
import { formatAccountDate } from "./account-format";

export function AccountJyotishiHome({
  readings,
  snapshot,
}: {
  readings: AccountReading[];
  snapshot?: AccountJyotishSnapshot | null;
}) {
  const current = readings[0];
  if (!current) return <NewRelationshipCard />;

  const lastActive = current.last_message_at || current.created_at;
  const jyotishi = current.assigned_jyotishi || "Your Personal AI Jyotishi";

  return (
    <section
      aria-labelledby="your-jyotishi-heading"
      className="mb-6 overflow-hidden rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] shadow-[var(--shadow-elevation-medium)]"
    >
      <div className="border-b border-[var(--color-border-light)] px-5 py-5 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
          Your AI Jyotishi
        </p>
        <h2 id="your-jyotishi-heading" className="mt-2 font-serif text-3xl leading-tight">
          {jyotishi} remembers where you left off
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-[var(--color-text-sub)]">
          Your kundali context and earlier questions stay together, so the next answer continues the
          same reading instead of starting again.
        </p>
      </div>

      {snapshot?.reading_id === current.reading_id ? (
        <TodayChartSnapshot snapshot={snapshot} />
      ) : null}

      <div className="grid gap-5 px-5 py-5 sm:grid-cols-[1fr_auto] sm:items-end sm:px-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-text-faint)]">
            Where you left off
          </p>
          <h3 className="mt-2 font-serif text-xl">{current.title}</h3>
          {current.last_message ? (
            <p className="mt-2 line-clamp-3 text-sm leading-6 text-[var(--color-text-sub)]">
              {current.last_message}
            </p>
          ) : (
            <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">
              Your kundali is saved and ready for the next question.
            </p>
          )}
          <p className="mt-3 text-xs text-[var(--color-text-faint)]">
            {current.conversation_turns
              ? `${current.conversation_turns} questions remembered`
              : "Kundali context saved"}
            {lastActive ? ` · Last active ${formatAccountDate(lastActive)}` : ""}
          </p>
        </div>
        <a
          href={current.chat_url}
          className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-accent)] px-5 py-3 text-sm font-semibold text-white"
        >
          Continue your reading
        </a>
      </div>
    </section>
  );
}

function TodayChartSnapshot({ snapshot }: { snapshot: AccountJyotishSnapshot }) {
  const isHindi = snapshot.language === "hi";
  const moonRashi = localizedName(snapshot.moon_rashi_en, snapshot.moon_rashi_hi, isHindi);
  const dasha = [
    periodName(snapshot.mahadasha, isHindi),
    periodName(snapshot.antardasha, isHindi),
  ].filter(Boolean);

  return (
    <div
      data-testid="account-jyotish-snapshot"
      className="mx-5 mt-5 rounded-xl border border-[var(--color-accent-border)] bg-[var(--color-surface-raised)] p-4 sm:mx-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
            {isHindi ? "Aaj aapki kundali mein" : "Today in your chart"}
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-faint)]">
            {isHindi ? "Engine-calculated graha facts" : "Engine-calculated chart facts"}
          </p>
        </div>
        <p className="text-xs text-[var(--color-text-faint)]">
          {formatAccountDate(snapshot.as_of)} · Lahiri sidereal
        </p>
      </div>

      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        <ChartFact label={isHindi ? "Chandra rashi" : "Moon sign"} value={moonRashi || "—"} />
        <ChartFact
          label={isHindi ? "Chal rahi dasha" : "Current dasha"}
          value={dasha.length ? dasha.join(" › ") : "—"}
        />
      </dl>

      {snapshot.transits.length ? (
        <div className="mt-4">
          <p className="text-xs font-semibold text-[var(--color-text-sub)]">
            {isHindi ? "Mukhya gochar" : "Key gochar"}
          </p>
          <ul className="mt-2 flex flex-wrap gap-2" aria-label="Current key transits">
            {snapshot.transits.map((transit) => (
              <li
                key={transit.planet_id}
                className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface-warm)] px-3 py-1.5 text-xs text-[var(--color-text-sub)]"
              >
                {transitLabel(transit, isHindi)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ChartFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-text-faint)]">{label}</dt>
      <dd className="mt-1 font-serif text-lg text-[var(--color-text-main)]">{value}</dd>
    </div>
  );
}

function periodName(period: AccountJyotishPeriod | null, isHindi: boolean): string {
  if (!period) return "";
  return localizedName(period.name_en, period.name_hi, isHindi);
}

function localizedName(english: string, hindi: string, isHindi: boolean): string {
  return isHindi ? hindi || english : english || hindi;
}

function transitLabel(transit: AccountJyotishTransit, isHindi: boolean): string {
  const planet = localizedName(transit.name_en, transit.name_hi, isHindi);
  const rashi = localizedName(transit.rashi_en, transit.rashi_hi, isHindi);
  const house = isHindi
    ? `Chandra se ${transit.house_from_moon} bhaav`
    : `${ordinal(transit.house_from_moon)} from Moon`;
  const retrograde = transit.is_retrograde ? (isHindi ? " · vakri" : " · retrograde") : "";
  return `${planet} · ${rashi} · ${house}${retrograde}`;
}

function ordinal(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

function NewRelationshipCard() {
  return (
    <section
      aria-labelledby="your-jyotishi-heading"
      className="mb-6 rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-6 shadow-[var(--shadow-elevation-medium)]"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
        Your AI Jyotishi
      </p>
      <h2 id="your-jyotishi-heading" className="mt-2 font-serif text-3xl leading-tight">
        Why does the same pattern keep returning?
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-[var(--color-text-sub)]">
        Generate your kundali once, then ask every question with the same chart context waiting here
        when you return.
      </p>
      <a
        href="/free-kundali/#kundali-form"
        className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-accent)] px-5 py-3 text-sm font-semibold text-white"
      >
        Generate your kundali
      </a>
    </section>
  );
}
